import React, { Fragment } from 'react';

import { useSelector } from 'react-redux';

import clsx from 'clsx';
import camelCase from 'lodash/camelCase';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import startCase from 'lodash/startCase';

// Bonnet Utils
import { useDevice } from '@bonnet/core/device';

// ATC-UI Utils & hooks
import { useFeatures } from '@atc/features';
import { formatNumber } from '@atc/string-fns';

import { ListColumns, SeeMore } from 'reaxl';

import { renderCustomLongFormContent, renderSummaryContent } from '@/utilities/crawlpath/CrawlpathRenderers';

import flpCitiesCrawlPathsDuck from '@/ducks/flp/flpCitiesCrawlPathsDuck';
import queryDuck from '@/ducks/queryDuck.js';
import srpAdditionalCrawlPathsDuck from '@/ducks/srp/srpAdditionalCrawlPathsDuck.js';
import srpCitiesCrawlPathsDuck from '@/ducks/srp/srpCitiesCrawlPathsDuck.js';
import srpContentDuck from '@/ducks/srp/srpContentDuck.js';
import srpCrawlPathsDataMapsDuck from '@/ducks/srp/srpCrawlPathsDataMapsDuck.js';
import srpDealershipsCrawlPathsDuck from '@/ducks/srp/srpDealershipsCrawlPathsDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpLocationTypeDuck from '@/ducks/srp/srpLocationTypeDuck.js';
import srpMakeCrawlPathsDuck from '@/ducks/srp/srpMakeCrawlPathsDuck.js';
import srpModelCrawlPathsDuck from '@/ducks/srp/srpModelCrawlPathsDuck.js';
import srpTrimCrawlPathsDuck from '@/ducks/srp/srpTrimCrawlPathsDuck';
import srpYearCrawlPathsDuck from '@/ducks/srp/srpYearCrawlPathsDuck.js';
import userDuck from '@/ducks/userDuck.js';

import CrawlPathNationalLinks from './CrawlPathNationalLinks';

const formatString = (string) => string.split(' ').join('-').toLowerCase();
const normalizeWhitespace = (string) => string?.replace(/\s+/g, ' ')?.trim() || '';

const isSingleQueryParam = (param) => param && !Array.isArray(param);

const listColumns = (items) => (
    <ListColumns
        className="list-unstyled"
        xs={1}
        sm={3}
        md={4}
        lg={4}
        items={items}
    />
);

const renderSeeMoreLinks = (label, items, device) => {
    const expanded = {
        label: 'View Fewer ' + label,
        glyph: 'menu-up',
    };
    const collapsed = {
        label: 'View All ' + label,
        glyph: 'menu-down',
    };
    const greaterThanSmall = _get(device, 'greaterThan.sm', false);
    const isSm = _get(device, 'is.sm', false);
    const isXs = _get(device, 'is.xs', false);

    if (greaterThanSmall && items.length < 41 || isSm && items.length < 19 || isXs && items.length < 4) {
        return listColumns(items);
    }
    return (
        <SeeMore
            collapsed={collapsed}
            expanded={expanded}
        >
            {listColumns(items)}
        </SeeMore>
    );
};

const priceFormat = (startingPrice) => {
    const priceValue = startingPrice && formatNumber({
        value: startingPrice,
        style: 'currency',
        currency: 'USD',
        formatPretty: true,
    });
    return ' ' + priceValue;
};

// Helper functions to check and return names for crawthpath link
const getMakeName = (makeKnown, makesMap, queryList) => (makeKnown && makesMap ? makesMap[queryList.makeCode.toUpperCase()] : '');
const getVehicleStyleNameAndSubstyle = (queryList, vehicleStylesMap, subStylesMap) => {
    let vehicleStyleSubtype = '';
    const vehicleStyleName = queryList.vehicleStyleCode && !Array.isArray(queryList.vehicleStyleCode) && vehicleStylesMap
        ? vehicleStylesMap[queryList.vehicleStyleCode.toUpperCase()] : '';

    switch (vehicleStyleName) {
        case ('Sedans'):
            if (!Array.isArray(queryList.sedanSubtypeCode)) { vehicleStyleSubtype = subStylesMap[queryList.sedanSubtypeCode] || ''; }
            break;

        case ('Suv / Crossovers'):
            if (!Array.isArray(queryList.suvSubtypeCode)) { vehicleStyleSubtype = subStylesMap[queryList.suvSubtypeCode] || ''; }
            break;

        case ('Vans'):
            if (!Array.isArray(queryList.canSubtypeCode)) { vehicleStyleSubtype = subStylesMap[queryList.canSubtypeCode] || ''; }
            break;

        case ('Trucks'):
            if (!Array.isArray(queryList.bodyStyleSubtypeCode)) { vehicleStyleSubtype = subStylesMap[queryList.bodyStyleSubtypeCode] || ''; }
            break;

        default:
    }

    return { vehicleStyleName, vehicleStyleSubtype };
};

const getModelName = (modelKnown, modelsMap, queryList) => (modelKnown && modelsMap ? modelsMap[queryList.modelCode] : '');
const getTrimName = (trimKnown, queryList) => (trimKnown ? queryList.trimCode?.split('|')?.[1] : '');
const getCarsText = (makeName, vehicleStyleName) => (makeName || vehicleStyleName ? '' : 'Cars');
const getFuelTypeName = (fuelTypeKnown, fuelTypeGroupMap, queryList) => (fuelTypeKnown && fuelTypeGroupMap ? fuelTypeGroupMap[queryList.fuelTypeGroup] : '');

const getExtColorName = (extColorKnown, extColorSimpleMap, queryList) => (extColorKnown && extColorSimpleMap ? extColorSimpleMap[queryList.extColorSimple] : '');

const getEngineName = (engineKnown, engineGroupMap, queryList) => (engineKnown && engineGroupMap ? engineGroupMap[queryList.engineCode] : '');

const getDriveTypeName = (driveTypeKnown, driveGroupMap, queryList) => (driveTypeKnown && driveGroupMap ? driveGroupMap[queryList.driveGroup] : '');

function VehicleCount({ count, startingPrice }) {
    return (
        <div className="text-size-200 margin-bottom-3">
            {`${count} for sale${(startingPrice && startingPrice !== 0 ? ' starting at ' + priceFormat(startingPrice) : '')}`}
        </div>
    );
}

function CrawlPathList({ id, qaId, heading, headingClasses, className = 'makes-listing-results', children }) {
    return (
        <div
            id={id}
            className={className}
        >
            {heading && (
                <h3
                    className={clsx(headingClasses, 'display-inline-block text-size-400 text-bold')}
                    data-qaid={qaId}
                >
                    {heading}
                </h3>
            )}
            {children}
        </div>
    );
}

function CrawlPathListItem({ link, title, qaId, linkText, count, startingPrice, includeMargin }) {
    return (
        <Fragment key={`crawlpath-${qaId}`}>
            <a
                href={link}
                title={title}
                data-qaid={formatString(qaId)}
                className={clsx({ 'display-block margin-bottom-4 text-size-200': includeMargin })}
            >
                {linkText}
            </a>
            {!!count && (
                <VehicleCount
                    count={count}
                    startingPrice={startingPrice}
                />
            )}
        </Fragment>
    );
}

function CrawlPathSectionContainer({ summaryContent, summaryContentData, children }) {
    return (
        <div
            data-qaid="cntr-crawl-path-footer"
            className="margin-bottom-5"
        >
            {renderCustomLongFormContent(summaryContentData)}

            {summaryContent && renderSummaryContent(summaryContentData)}

            {children}
        </div>
    );
}

function CrawlPathLinks() {
    const device = useDevice();

    const hasZip = useSelector((state) => _has(srpFiltersDuck.selectors.getFiltersValues(state), 'zip', false));

    const { conditionData, pricingData, stylesData } = useSelector(srpAdditionalCrawlPathsDuck.selectors.getDuckState);
    const { yearsData } = useSelector(srpYearCrawlPathsDuck.selectors.getDuckState);
    const { makesData } = useSelector(srpMakeCrawlPathsDuck.selectors.getDuckState);
    const { modelData } = useSelector(srpModelCrawlPathsDuck.selectors.getDuckState);
    const { trimData } = useSelector(srpTrimCrawlPathsDuck.selectors.getDuckState);
    const { dealershipsData } = useSelector(srpDealershipsCrawlPathsDuck.selectors.getDuckState);
    const { summaryContentData, srpSelfServiceSeo } = useSelector(srpContentDuck.selectors.getDuckState);
    const { makesMap, vehicleStylesMap, modelsMap, subStyleMap, fuelTypeGroupMap, extColorSimpleMap, engineGroupMap, driveGroupMap } = useSelector(srpCrawlPathsDataMapsDuck.selectors.getDuckState);
    const isNational = useSelector(srpLocationTypeDuck.selectors.getDuckState);
    const queryList = useSelector(queryDuck.selectors.getDuckState);
    const userLocation = useSelector(userDuck.selectors.getLocation);

    // cities data may come from flp cities duck
    const { citiesData: srpCitiesData } = useSelector(srpCitiesCrawlPathsDuck.selectors.getDuckState);
    const { citiesData: flpCitiesData } = useSelector(flpCitiesCrawlPathsDuck.selectors.getDuckState);
    const citiesData = !_isEmpty(srpCitiesData) ? srpCitiesData : flpCitiesData;
    const {
        indexable_locations: indexableLocations = 'high',
        links: altezzaLinks = {},
    } = srpSelfServiceSeo || {};

    const hasCrawlPathData = !_isEmpty(makesData) || !_isEmpty(modelData) || !_isEmpty(yearsData) || !_isEmpty(trimData) || !_isEmpty(citiesData) || !_isEmpty(dealershipsData) || !_isEmpty(altezzaLinks);

    const {
        crawlPath: [enableCrawlPath, {
            showYearForTrimList,
            summaryContent,
            showVehicleStyleName,
            showFullTitleCityCrawlPath,
        }],
        enable_altezza_content_only: [enableAltezzaContentOnly],
        enable_indexed_locations: [enableIndexedLocations],

    } = useFeatures(['crawlPath', 'enable_altezza_content_only', 'enable_indexed_locations']);

    const listingTypeTitle = queryList.listingType && startCase(camelCase(queryList.listingType)) || '';

    const locationIndexValue = enableIndexedLocations ? indexableLocations : 'all';
    const isHighIndexable = locationIndexValue === 'high' || locationIndexValue === 'highest';

    const makeKnown = isSingleQueryParam(queryList.makeCode);
    const modelKnown = isSingleQueryParam(queryList.modelCode);
    const trimKnown = isSingleQueryParam(queryList.trimCode);
    const fuelTypeKnown = isSingleQueryParam(queryList.fuelTypeGroup);
    const extColorKnown = isSingleQueryParam(queryList.extColorSimple);
    const engineKnown = isSingleQueryParam(queryList.engineCode);
    const driveTypeKnown = isSingleQueryParam(queryList.driveGroup);

    const makeName = getMakeName(makeKnown, makesMap, queryList);
    const { vehicleStyleName, vehicleStyleSubtype } = getVehicleStyleNameAndSubstyle(queryList, vehicleStylesMap, subStyleMap);
    const modelName = getModelName(modelKnown, modelsMap, queryList);
    const trimName = getTrimName(trimKnown, queryList);
    const fuelTypeName = getFuelTypeName(fuelTypeKnown, fuelTypeGroupMap, queryList);
    const extColorName = getExtColorName(extColorKnown, extColorSimpleMap, queryList);
    const engineName = getEngineName(engineKnown, engineGroupMap, queryList);
    const driveTypeName = getDriveTypeName(driveTypeKnown, driveGroupMap, queryList);

    const searchTitle = vehicleStyleName ? `${makeName} ${vehicleStyleSubtype} ${vehicleStyleName}` : `${makeName} ${modelName} ${getCarsText(makeName, vehicleStyleName)}`;
    const sectionTitle = `${makeName} ${modelName} ${trimName} ${getCarsText(makeName)} for Sale by Year`.replace(/\s+/g, ' ').trim();
    const yearParam = queryList.startYear || '';
    const buyOnlineFilter = 'BUY_ONLINE';

    const getCityState = () => {
        const { cityState } = citiesData;
        return cityState ? ` in ${cityState}` : ' For Sale';
    };

    const isValidCrawlData = (data) => !_isEmpty(data) && data?.links?.length > 0 && data?.links?.[0]?.link;

    const isValidAltezzaCrawlData = (data) => !_isEmpty(data) && data?.length > 0 && data?.[0]?.href;

    const bodyStyleName = (defaultValue) => (showVehicleStyleName && vehicleStyleName) || defaultValue;

    const hasBuyOnlineFilter = queryList?.homeServices && queryList?.homeServices?.includes(buyOnlineFilter);
    const entireUrlRegex = /^https?:\/\/|^\/\//i;

    // Altezza crawlpath links additions to canonical URLs
    const renderAltezzaCrawlPathLinks = () => {
        const crawlPathLinks = altezzaLinks?.crawlpath || [];
        const shouldRenderCount = altezzaLinks?.crawlpath?.every((item) => _has(item, 'listingCount'));

        const items = crawlPathLinks.length > 0 ? crawlPathLinks.map((item) => {

            // whether receiving the href that containing protocol or not , the final link should be processed pathname and query absolutely before passing to prop
            let link = '';
            const isEntireURL = entireUrlRegex.test(item?.href);
            const url = isEntireURL ? new URL(item?.href) : new URL(item?.href, 'https://www.autotrader.com/');
            link = url.pathname + url.search;

            return (
                <CrawlPathListItem
                    {...item}
                    count={shouldRenderCount ? item?.listingCount : null}
                    title={item?.text}
                    key={item?.id}
                    qaId={`lnk-altezza-canonical-${item?.id}`}
                    linkText={`${item?.text}`.replace(/\s+/g, ' ').trim()}
                    link={link}
                    includeMargin={!shouldRenderCount}
                />
            );
        }) : null;
        return (
            <CrawlPathList
                id="altezzaCanonicalListingResults"
                className="altezza-canonical-listing-results"
                headingClasses="margin-top-4 margin-bottom-0"
                qaId="lbl-SRPCrawlPathAltezzaCanonicalFooterTitle"
                heading="Similar Vehicles for Sale"
            >
                {crawlPathLinks?.length > 0 && renderSeeMoreLinks('Similar Vehicles', items, device)}
            </CrawlPathList>
        );
    };

    const renderDealerships = () => {
        if (_isEmpty(dealershipsData) || !dealershipsData.links || dealershipsData.links?.length === 0 || !queryList.state) {
            return null;
        }

        const { state } = queryList;
        const items = dealershipsData?.links?.map((item) => (
            <CrawlPathListItem
                {...item}
                link={item?.link?.href}
                key={item?.owner?.name}
                title={item?.owner?.name}
                qaId={`lnk-dealer-${item?.owner?.name}`}
                linkText={`${item?.owner?.name}`.replace(/\s+/g, ' ').trim()}
            />
        ));

        return (
            <CrawlPathList
                id="topDealersListingResults"
                className="dealers-listing-results"
                qaId="lbl-SRPCrawlPathDealersFooterTitle"
                heading={`Local Dealerships in ${state}`}
            >
                {dealershipsData?.links?.length > 0 && renderSeeMoreLinks('topDealers', items, device)}
            </CrawlPathList>
        );
    };

    // Years crawlpath for National/Local
    const renderModelYearsList = () => {
        const items = yearsData?.links?.map((item) => (
            <CrawlPathListItem
                {...item}
                key={item.link}
                title={item.value}
                qaId={`lnk-model-${item.value}`}
                linkText={`${item.value} ${makeName} ${modelName} ${trimName} ${getCityState()}`}
            />
        ));

        return (
            <CrawlPathList
                id="yearsListingResults"
                qaId="lbl-SRPCrawlPathYearsFooterTitle"
                className="years-listing-results"
                heading={sectionTitle}
            >
                <div className="model-years margin-bottom-3">
                    {listColumns(items)}
                </div>
            </CrawlPathList>
        );
    };

    // Makes Crawlpaths
    const renderMakesList = () => {
        const items = makesData?.links?.map((item) => (
            <CrawlPathListItem
                {...item}
                key={item.value}
                title={item.value}
                qaId={`lnk-make-${item.value}`}
                linkText={`${listingTypeTitle} ${yearParam} ${item.value} ${bodyStyleName('')} For Sale`.replace(/\s+/g, ' ').trim()}
            />
        ));

        return (
            <CrawlPathList
                id="makesListingResults"
                qaId="lbl-SRPCrawlPathFooterTitle"
                heading={`${listingTypeTitle} ${yearParam} ${searchTitle} for Sale by Make`.replace(/\s+/g, ' ').trim()}
            >
                {makesData?.links?.length > 0 && renderSeeMoreLinks('Makes', items, device)}
            </CrawlPathList>
        );
    };

    // Model Crawlpaths
    const renderModelList = () => {
        const headingTitle = `${yearParam} ${searchTitle}`.replace(/\s+/g, ' ').trim();
        const items = modelData?.links?.map((item) => (
            <CrawlPathListItem
                {...item}
                key={item.value}
                title={item.value}
                qaId={`lnk-model-${item.value}`}
                linkText={`${listingTypeTitle} ${yearParam} ${makeName} ${item.value} For Sale`.replace(/\s+/g, ' ').trim()}
            />
        ));

        return (
            <CrawlPathList
                id="modelListingResults"
                qaId="lbl-SRPCrawlPathFooterTitle"
                heading={`${listingTypeTitle} ${headingTitle} for Sale by Model`.replace(/\s+/g, ' ').trim()}
            >
                {modelData?.links?.length > 0 && renderSeeMoreLinks('Models', items, device)}
            </CrawlPathList>
        );
    };

    // Trims crawlpaths
    const renderTrimList = () => {
        const yearForTrim = (enableCrawlPath && showYearForTrimList && queryList?.startYear) ? yearParam : '';
        const headingTitle = `${listingTypeTitle} ${yearForTrim} ${makeName} ${modelName} ${getCarsText(makeName)} for Sale by Trim`.replace(/\s+/g, ' ').trim();

        // For FBA, trim list is still rendered when user filters by a trim, but the trim they filtered will be excluded in the list
        const filteredTrimData = trimName ? trimData?.links?.filter((item) => item.value?.split('|')?.[1] !== trimName) : trimData?.links;
        const items = filteredTrimData?.map((item) => {
            // create full trim title with year make and model (E.g. 'CIVIC|Si' => '2020 Honda Civic Si')
            const trimTitle = `${listingTypeTitle} ${yearForTrim} ${makeName} ${modelName} ${item.value?.split('|')?.[1]} For Sale`;
            return (
                <CrawlPathListItem
                    {...item}
                    key={item.value}
                    title={item.value}
                    qaId={`lnk-trim-${item.value}`}
                    linkText={trimTitle.replace(/\s+/g, ' ').trim()}
                />
            );
        });

        return (
            <CrawlPathList
                id="modelListingResults"
                qaId="lbl-SRPCrawlPathFooterTitle"
                heading={headingTitle}
            >
                {trimData?.links?.length > 0 && renderSeeMoreLinks('Trims', items, device)}
            </CrawlPathList>
        );
    };

    /*  Render All Cities crawlpath for Local and Major Cities crawlpath for National  */
    const renderCitiesList = () => {
        const vehicleDetailsTitle = (showFullTitleCityCrawlPath && isHighIndexable) ? `${listingTypeTitle} ${yearParam} ${extColorName} ${driveTypeName} ${engineName} ${fuelTypeName}` : `${listingTypeTitle} ${yearParam}`;
        const locationTitle = makeName ? `${makeName} ${modelName} ${vehicleStyleName} ${trimName}` : `${bodyStyleName('Cars')}`;
        const items = citiesData?.links?.map((item) => (
            <CrawlPathListItem
                {...item}
                key={item.city}
                title={item.city}
                qaId={`lnk-city-${item.city}`}
                linkText={normalizeWhitespace(`${vehicleDetailsTitle} ${locationTitle} For Sale in ${item.city}`)}
            />
        ));

        const cityNationalTitle = (!isNational && userLocation?.state) ? 'in Nearby Cities' : '';
        const headingTitle = normalizeWhitespace(`${vehicleDetailsTitle} ${makeName} ${modelName} ${vehicleStyleSubtype} ${vehicleStyleName} ${trimName} ${getCarsText(makeName, vehicleStyleName)} ${(!isNational && userLocation?.state) ? '' : 'for Sale by City'} ${cityNationalTitle}`);

        return (
            <CrawlPathList
                id="citiesListingResults"
                className="cities-listing-results"
                headingClasses="margin-top-4 margin-bottom-0"
                qaId="lbl-SRPCrawlPathCitiesFooterTitle"
                heading={headingTitle}
            >
                {citiesData?.links?.length > 0 && renderSeeMoreLinks('Cities', items, device)}
            </CrawlPathList>
        );
    };

    if (enableCrawlPath && hasCrawlPathData) {
        return (
            <CrawlPathSectionContainer
                summaryContent={summaryContent}
                summaryContentData={summaryContentData}
            >
                <hr className="margin-top-5" />
                {!!isValidAltezzaCrawlData(altezzaLinks?.crawlpath) && renderAltezzaCrawlPathLinks()}
                {!!isValidCrawlData(yearsData) && !enableAltezzaContentOnly && !hasBuyOnlineFilter && renderModelYearsList()}
                {!!isValidCrawlData(trimData) && !enableAltezzaContentOnly && !hasBuyOnlineFilter && renderTrimList()}
                {!!isValidCrawlData(modelData) && !enableAltezzaContentOnly && !hasBuyOnlineFilter && renderModelList()}
                {!!isValidCrawlData(makesData) && !enableAltezzaContentOnly && !hasBuyOnlineFilter && renderMakesList()}
                {!!isValidCrawlData(citiesData) && !enableAltezzaContentOnly && !hasBuyOnlineFilter && renderCitiesList()}

                {!enableAltezzaContentOnly && !hasBuyOnlineFilter && (
                    <CrawlPathNationalLinks
                        conditionData={conditionData}
                        pricingData={pricingData}
                        stylesData={stylesData}
                        hasZip={hasZip}
                    />
                )}
                {dealershipsData && !enableAltezzaContentOnly && !hasBuyOnlineFilter && renderDealerships()}
            </CrawlPathSectionContainer>
        );
    }

    if (enableCrawlPath && (!_isEmpty(citiesData) || (summaryContentData && Object.keys(summaryContentData).length > 0))) {
        // Return city crawlpath only for non-canonical zip pages
        return (

            <CrawlPathSectionContainer
                summaryContent={summaryContent}
                summaryContentData={summaryContentData}
            >
                <hr className="margin-top-5" />
                {isValidCrawlData(citiesData) && !enableAltezzaContentOnly && !hasBuyOnlineFilter && renderCitiesList()}
            </CrawlPathSectionContainer>
        );
    }

    return null;
}

export default CrawlPathLinks;
